import { Store, GetterTree, MutationTree, ActionTree, Module } from "vuex";

import { RootState } from "..";
import container from "../../inversify.config";
import { IContentSetVersionState } from "../states/content-set-version-state";
import { ContentSetVersionModel } from "../../models/content/content-set-version.model";
import { IContentSetVersion } from "../../models/interfaces/content/content-set-version.model.interface";
import { contentSetVersionService } from "../../services/content-set-version.service";
import PerformanceService from "../../services/performance.service";
import { IContentSetPerformance } from "../../models/interfaces/content/content-set-performance.model.interface";
import { IMultivariateConfigurationRequest } from "../../models/interfaces/multivariate/multivariate-configuration-request.model.interface";
import { IGroupedPerformanceSummary } from "../../models/interfaces/performance/grouped-performance-summary.model.interface";
import { IContentTemplate } from "../../models/interfaces/content/content-template.model.interface";
import { IContentSetVersionTemplatesRequest } from "../../models/interfaces/content/content-set-version-templates-request.model.interface";
import { IContentSetVersionTemplate } from "../../models/interfaces/content/content-set-version-template.model.interface";
import { ContentSetPerformance } from "@/models/content/content-set-performance.model";
import EnquiryService from "@/services/enquiry.service";
import { IContentSetVersionEnquirySummary } from "@/models/interfaces/enquiry/content-set-version-enquiry-summary.model.interface";
import { CreativeEnquirySummary } from "@/models/enquiry/creative-enquiry-summary.model";

export const state: IContentSetVersionState = {
    previous: [],
    live: new ContentSetVersionModel(),
    configuration: new ContentSetVersionModel(),
};

export const getters: GetterTree<IContentSetVersionState, RootState> = {
    previous(state) {
        return state.previous;
    },
    live(state) {
        return state.live;
    },
    configuration(state) {
        return state.configuration;
    }
};

export const mutations: MutationTree<IContentSetVersionState> = {
    previous(state, previous: IContentSetVersion[]) {
        state.previous = previous;
    },
    live(state, live: IContentSetVersion) {
        state.live = live;
    },
    configuration(state, configuration: IContentSetVersion) {
        state.configuration = configuration;
    },
    livePerformance(state, livePerformance: IContentSetPerformance[]) {
        if (livePerformance !== null) {
            state.live.templates.forEach((p) => {
                const performance = livePerformance.filter((a) => a.id === p.templateId)[0];

                if (performance) {
                    p.performance = performance;
                } else {
                    p.performance = new ContentSetPerformance();
                }
            });
        }
    },
    liveEnquiryPerformance(state, data: IContentSetVersionEnquirySummary) {
        if (data !== null) {
            state.live.templates.forEach((p) => {
                const performance = data.templates.filter((a) => a.templateId === p.templateId)[0];

                if (performance) {
                    p.enquiry = performance;
                } else {
                    p.enquiry = new CreativeEnquirySummary(p.templateId);
                }
            });
        }
    },
    previousPerformance(state, previousPerformance: IGroupedPerformanceSummary[]) {
        if (previousPerformance !== null) {
            const previous = state.previous.map((x) => x);
            previous.forEach((p) => {
                const versionPerformance = previousPerformance.filter((a) => a.id === p.contentSetVersionId)[0];

                if (versionPerformance && versionPerformance.summaries) {
                    p.templates.forEach((t) => {
                        const performance = versionPerformance.summaries.filter((a) => a.id === t.templateId)[0];

                        if (performance) {
                            t.performance = performance;
                        } else {
                            t.performance = new ContentSetPerformance();
                        }
                    });
                } else {
                    p.templates.forEach((t) => { t.performance = new ContentSetPerformance(); });
                }
            });

            state.previous.splice(0, state.previous.length);
            state.previous = previous;
        }
    },
    previousEnquiryPerformance(state, data: IContentSetVersionEnquirySummary[]) {
        if (data !== null) {
            const previous = state.previous.map((x) => x);
            previous.forEach((p) => {
                const versionPerformance = data.filter((a) => a.contentSetVersionId === p.contentSetVersionId)[0];

                if (versionPerformance && versionPerformance.templates) {
                    p.templates.forEach((t) => {
                        const performance = versionPerformance.templates.filter((a) => a.templateId === t.templateId)[0];

                        if (performance) {
                            t.enquiry = performance;
                        } else {
                            t.enquiry = new CreativeEnquirySummary(t.templateId);
                        }
                    });
                }
            });

            state.previous.splice(0, state.previous.length);
            state.previous = previous;
        }
    },
};

export const actions: ActionTree<IContentSetVersionState, RootState> = {
    async retrieveLiveVersion({ commit, dispatch, rootState }) {
        return new Promise((resolve) => {
            contentSetVersionService.GetLive(rootState.contentSetModule.contentSet.contentSetId)
                .then((value) => {
                    if (value.result) {
                        commit("live", value.result);
                        Promise.race([
                            dispatch("retrieveLivePerformance"),
                            dispatch("retrieveLiveEnquiryPerformance")
                        ]).then((value) => {
                            resolve(value);
                        });
                    } else {
                        resolve(false);
                    }
                });
        });
    },
    async retrieveLivePerformance({ commit, state }, ) {
        return new Promise((resolve) => {
            container.get<PerformanceService>(PerformanceService)
                .getContentSetVersionPerformance(state.live.contentSetVersionId)
                .then((value) => {
                    commit("livePerformance", value.result);
                    resolve(value.success);
                });
        });
    },
    async retrieveLiveEnquiryPerformance({ commit, rootState }) {
        return new Promise((resolve) => {
            container.get<EnquiryService>(EnquiryService)
                .getLiveContentSetVersionEnquiries(rootState.contentSetModule.contentSet.contentSetId)
                .then((value) => {
                    commit("liveEnquiryPerformance", value.result);
                    resolve(true);
                });
        });
    },
    async retrievePreviousVersions({ commit, dispatch, rootState }) {
        return new Promise((resolve) => {
            const contentSetId = rootState.contentSetModule.contentSet.contentSetId;
            if (contentSetId > 0) {
                contentSetVersionService.GetPrevious(contentSetId)
                    .then((value) => {
                        commit("previous", value.result);
                        dispatch("retrievePreviousPerformance").then(() => {
                            dispatch("retrievePreviousEnquiryPerformance").then((value) => {
                                resolve(value);
                            });
                        });
                    });
            } else {
                resolve(false);
            }
        });
    },
    async retrievePreviousPerformance({ commit, rootState }, ) {
        return new Promise((resolve) => {
            container.get<PerformanceService>(PerformanceService)
                .getPreviousContentSetVersionPerformance(rootState.contentSetModule.contentSet.contentSetId)
                .then((value) => {
                    commit("previousPerformance", value.result);
                    resolve(value.success);
                });
        });
    },
    async retrievePreviousEnquiryPerformance({ commit, rootState }) {
        return new Promise((resolve) => {
            container.get<EnquiryService>(EnquiryService)
                .getPreviousContentSetVersionEnquiries(rootState.contentSetModule.contentSet.contentSetId)
                .then((value) => {
                    commit("previousEnquiryPerformance", value.result);
                    resolve(true);
                });
        });
    },
    async retrieveConfigurationVersion({ commit, rootState }) {
        return new Promise((resolve) => {
            contentSetVersionService.GetConfiguration(rootState.contentSetModule.contentSet.contentSetId)
                .then((value) => {
                    commit("configuration", value.result);
                    resolve(value.success);
                });
        });
    },
    async createConfiguration({ dispatch, rootState }, name: string) {
        return new Promise((resolve) => {
            contentSetVersionService.CreateConfiguration(rootState.contentSetModule.contentSet.contentSetId, name)
                .then((value) => {
                    if (value) {
                        dispatch("retrieveConfigurationVersion").then((value) => {
                            resolve(value.success);
                        });
                    } else {
                        resolve(false);
                    }
                });
        });
    },
    async updateMultivariateConfig({ dispatch, state }, multivariate: IMultivariateConfigurationRequest) {
        return new Promise((resolve) => {
            contentSetVersionService.UpdateMultivariateConfig(state.configuration.contentSetVersionId, multivariate)
                .then((value) => {
                    if (value) {
                        dispatch("retrieveConfigurationVersion").then((value) => {
                            resolve(value);
                        });
                    } else {
                        resolve(false);
                    }
                });
        });
    },
    async removeTemplate({ dispatch, state }, templateId: number) {
        return new Promise((resolve) => {
            contentSetVersionService.RemoveTemplate(state.configuration.contentSetVersionId, templateId)
                .then((value) => {
                    if (value) {
                        dispatch("retrieveConfigurationVersion").then((value) => {
                            resolve(true);
                        });
                    } else {
                        resolve(false);
                    }
                });
        });
    },
    async createTemplate({ dispatch, rootState }, template: IContentTemplate) {
        return new Promise((resolve) => {
            dispatch("contentTemplateModule/createTemplate", template, { root: true }).then((value) => {
                if (!value) {
                    resolve(false);
                } else {
                    const rootTemplate = rootState.contentTemplateModule.template;
                    contentSetVersionService.AddTemplate(state.configuration.contentSetVersionId, rootTemplate.templateId)
                        .then((value) => {
                            if (value) {
                                dispatch("retrieveConfigurationVersion").then((value) => {
                                    resolve(true);
                                });
                            } else {
                                resolve(false);
                            }
                        });
                }
            });
        });
    },
    async updateMultivariatePercentages({ dispatch, state }, templates: IContentSetVersionTemplate[]) {
        return new Promise((resolve) => {
            const request: IContentSetVersionTemplatesRequest = { templates };
            contentSetVersionService.UpdateMultivariatePercentages(state.configuration.contentSetVersionId, request)
                .then((value) => {
                    if (value) {
                        dispatch("retrieveConfigurationVersion").then((value) => {
                            resolve(value);
                        });
                    } else {
                        resolve(false);
                    }
                });
        });
    },
    async promoteConfiguration({ dispatch, rootState }) {
        return new Promise((resolve) => {
            contentSetVersionService.PromoteConfiguration(rootState.contentSetModule.contentSet.contentSetId)
                .then((value) => {
                    if (value) {
                        Promise.race([
                            dispatch("retrieveLiveVersion"),
                            dispatch("retrievePreviousVersions"),
                            dispatch("retrieveConfigurationVersion")
                        ]).then((value) => {
                            resolve(value);
                        });
                    } else {
                        resolve(false);
                    }
                });
        });
    },
    async promoteWinner({ dispatch, rootState }) {
        return new Promise((resolve) => {
            contentSetVersionService.PromoteWinner(rootState.contentSetModule.contentSet.contentSetId)
                .then((value) => {
                    if (value) {
                        Promise.race([
                            dispatch("retrieveLiveVersion"),
                            dispatch("retrievePreviousVersions"),
                            dispatch("retrieveConfigurationVersion")
                        ]).then((value) => {
                            resolve(value);
                        });
                    } else {
                        resolve(false);
                    }
                });
        });
    }
};

export const contentSetVersionModule: Module<IContentSetVersionState, RootState> = {
    state, getters, mutations, actions, namespaced: true
};
